import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeftRight,
  ChartColumnIncreasing,
  ChevronRight,
  CircleAlert,
  Clipboard,
  Clock3,
  LogOut,
  Menu,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  ShieldCheck,
  Users,
  X,
} from "lucide-react";
import type {
  AgentView,
  AuthConfig,
  AuthResult,
  DemoPersona,
  DeskScript,
  Disposition,
  Duty,
  Intent,
  QueueItem,
  Role,
  Snapshot,
  Status,
  Team,
  Transfer,
  User,
} from "../shared/contracts";
import {
  canAccessDashboard,
  DISPOSITION_LABELS,
  INTENT_LABELS,
  STATUS_LABELS,
} from "../shared/contracts";
import {
  ApiError,
  clearSession,
  hasWebSession,
  isExtension,
  request,
  saveSession,
} from "./api";
import { AdminDirectory } from "./AdminDirectory";
import { AdminSetup } from "./AdminSetup";
import { ActiveCallProbeStatus } from "./ActiveCallProbeStatus";
import { Analytics } from "./Analytics";
import { DashboardOverview } from "./DashboardOverview";
import { GhlSignIn } from "./GhlSignIn";
import {
  canRequestHandoff,
  compareDispatchAgents,
  matchesDispatchFilter,
  rosterCsv,
  monitoredAgents,
  statusSeconds,
} from "./dashboard-data";
import { AgentDispatchDetail, ManagerRosterRow } from "./ManagerRoster";
import { effectiveStatus, presenceDescription, serverNow } from "./presence";
import { useConnectionStatus } from "./use-connection";
import { useBoundedPoll } from "./use-bounded-poll";
import { canCreateScript, canEditScript } from "./script-access";

type Page = "team" | "analytics" | "queue" | "scripts" | "manage";
type RosterGroup = "all" | "closer" | "bdr";
type CreateRequest = { id: string; createdAt: number; uncertain?: boolean };
type Modal =
  | { kind: "transfer"; agent: AgentView }
  | { kind: "queue"; item?: QueueItem; createRequest?: CreateRequest }
  | { kind: "script"; item?: DeskScript; createRequest?: CreateRequest }
  | { kind: "user"; user?: User }
  | { kind: "team"; team?: Team }
  | null;
const intents: Intent[] = ["ready", "break", "lunch", "training", "meeting"];
const dispositions: Disposition[] = [
  "sale",
  "callback",
  "not_interested",
  "wrong_number",
  "no_answer",
  "other",
];
const roles: Role[] = ["member", "lead", "manager", "admin"];
const duties: Duty[] = ["opener", "closer", "other"];
const statusOptions: Status[] = [
  "available",
  "busy",
  "wrap_up",
  "reserved",
  "break",
  "lunch",
  "training",
  "meeting",
  "offline",
  "unknown",
];
const label = (value: string) =>
  value.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
const dateTime = (value: number | null) =>
  value
    ? new Date(value).toLocaleString([], {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "Not scheduled";
const age = (value: number | null, now: number) => {
  if (!value) return "not observed";
  const seconds = Math.max(0, Math.floor((now - value) / 1000));
  return seconds < 60
    ? `${seconds}s ago`
    : seconds < 3600
      ? `${Math.floor(seconds / 60)}m ago`
      : `${Math.floor(seconds / 3600)}h ago`;
};
const duration = (ms: number) =>
  `${String(Math.floor(Math.max(0, ms) / 60000)).padStart(2, "0")}:${String(
    Math.floor(Math.max(0, ms) / 1000) % 60,
  ).padStart(2, "0")}`;
const nameFor = (snapshot: Snapshot, id: string) =>
  snapshot.agents.find((a) => a.id === id)?.name ??
  (snapshot.viewer.id === id ? snapshot.viewer.name : "Agent");
const isActiveTransfer = (transfer: Transfer, now: number) =>
  ["pending", "accepted"].includes(transfer.state) && transfer.expiresAt > now;
const safeUrl = (value: string) => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:" &&
      parsed.hostname === "app.gohighlevel.com"
      ? parsed.href
      : null;
  } catch {
    return null;
  }
};
const taskDue = (item: QueueItem, now: number) =>
  item.dueAt !== null && item.dueAt <= now && item.status === "open";

function ModalShell({
  title,
  description,
  error,
  onClose,
  children,
}: {
  title: string;
  description?: string;
  error?: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const returnFocus = useRef<HTMLElement | null>(
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null,
  );
  useEffect(() => {
    const dialog = ref.current;
    dialog?.showModal();
    return () => {
      dialog?.close();
      window.setTimeout(() => {
        if (!dialog?.open && returnFocus.current?.isConnected)
          returnFocus.current.focus();
      }, 0);
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className="desk-dialog"
      aria-label={title}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <div className="dialog-heading">
        <div>
          <h2>{title}</h2>
          {description && <p>{description}</p>}
        </div>
        <button
          type="button"
          className="icon-button"
          aria-label="Close dialog"
          onClick={onClose}
        >
          <X size={20} />
        </button>
      </div>
      {error && (
        <div className="dialog-error" role="alert">
          <CircleAlert size={17} />
          <span>{error}</span>
        </div>
      )}
      {children}
    </dialog>
  );
}

function StatusMark({ status }: { status: Status }) {
  return (
    <span
      className={`status-mark status-${status}`}
      aria-label={STATUS_LABELS[status]}
    >
      <span className="status-symbol" aria-hidden="true">
        {status === "available"
          ? "●"
          : status === "busy"
            ? "◕"
            : status === "wrap_up"
              ? "◫"
              : status === "unknown"
                ? "?"
                : status === "offline"
                  ? "×"
                  : status === "reserved"
                    ? "◆"
                    : "Ⅱ"}
      </span>
      {STATUS_LABELS[status]}
    </span>
  );
}

function ErrorNotice({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="notice notice-error" role="alert">
      <CircleAlert size={18} />
      <span>{message}</span>
      {onRetry && (
        <button type="button" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

function TransferActions({
  transfer,
  snapshot,
  now,
  stale,
  busy,
  act,
}: {
  transfer: Transfer;
  snapshot: Snapshot;
  now: number;
  stale: boolean;
  busy: boolean;
  act: (
    transfer: Transfer,
    action: "accept" | "decline" | "cancel" | "complete",
  ) => void;
}) {
  const incoming = transfer.toUserId === snapshot.viewer.id;
  const expired =
    transfer.expiresAt <= now &&
    ["pending", "accepted"].includes(transfer.state);
  return (
    <article className="transfer-card">
      <div className="row-between">
        <strong>
          {incoming
            ? `From ${nameFor(snapshot, transfer.fromUserId)}`
            : `To ${nameFor(snapshot, transfer.toUserId)}`}
        </strong>
        <span
          className={`transfer-state ${expired ? "expired" : transfer.state}`}
        >
          {expired ? "Expired" : label(transfer.state)}
        </span>
      </div>
      <p className="wrap-text">{transfer.summary}</p>
      <p className="meta">
        {expired
          ? "Reservation expired"
          : ["pending", "accepted"].includes(transfer.state)
            ? `Time left ${duration(transfer.expiresAt - now)}`
            : `Updated ${dateTime(transfer.updatedAt)}`}
      </p>
      {transfer.state === "accepted" && !expired && (
        <p className="inline-guidance">
          Accepted. Complete the call transfer in GHL, then mark coordination
          complete here.
        </p>
      )}
      {transfer.state === "pending" && !expired && (
        <p className="inline-guidance">
          This request coordinates the handoff in GHL Connect. It does not start
          a GHL transfer.
        </p>
      )}
      {!expired && (
        <div className="button-row">
          {incoming && transfer.state === "pending" && (
            <>
              <button
                type="button"
                disabled={busy || stale}
                onClick={() => act(transfer, "accept")}
              >
                Accept
              </button>
              <button
                type="button"
                className="secondary"
                disabled={busy || stale}
                onClick={() => act(transfer, "decline")}
              >
                Decline
              </button>
            </>
          )}
          {!incoming && transfer.state === "pending" && (
            <button
              type="button"
              className="secondary"
              disabled={busy || stale}
              onClick={() => act(transfer, "cancel")}
            >
              Cancel request
            </button>
          )}
          {transfer.state === "accepted" && (
            <>
              <button
                type="button"
                disabled={busy || stale}
                onClick={() => act(transfer, "complete")}
              >
                Mark handoff complete
              </button>
              {!incoming && (
                <button
                  type="button"
                  className="secondary"
                  disabled={busy || stale}
                  onClick={() => act(transfer, "cancel")}
                >
                  Cancel handoff
                </button>
              )}
            </>
          )}
        </div>
      )}
      {stale && ["pending", "accepted"].includes(transfer.state) && (
        <p className="field-hint">
          Actions pause until GHL Connect reconnects.
        </p>
      )}
    </article>
  );
}

function Login({
  onLogin,
  onGhlLogin,
}: {
  onLogin: (result: AuthResult) => void;
  onGhlLogin: (user: User) => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [personas, setPersonas] = useState<DemoPersona[]>([]);
  const [environment, setEnvironment] = useState<
    "demo" | "local" | "server" | null
  >(null);
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      const health = await request<{
        ok: true;
        environment: "demo" | "local" | "server";
      }>("GET", "/api/health");
      setEnvironment(health.environment);
      const config = await request<AuthConfig>("GET", "/api/auth/config");
      setAuthConfig(config);
      setError("");
      if (health.environment === "demo") {
        const response = await request<{ personas: DemoPersona[] }>(
          "GET",
          "/api/demo/personas",
        );
        setPersonas(response.personas);
      } else setPersonas([]);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Cannot reach GHL Connect",
      );
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const login = async (path: string, body: unknown) => {
    setBusy(true);
    setError("");
    try {
      onLogin(await request<AuthResult>("POST", path, body));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="login-layout">
      <section className="login-brand">
        <p className="eyebrow">GHL CONNECT / OPERATIONS</p>
        <h1>GHL Connect</h1>
        <p>
          Know who is ready. Coordinate a handoff. Keep the next follow-up in
          view.
        </p>
        <div className="login-ledger" aria-hidden="true">
          <span>TEAM</span>
          <span>STATUS</span>
          <span>NEXT ACTION</span>
          <i />
          <i />
          <i />
          <i />
          <i />
          <i />
          <i />
          <i />
          <i />
        </div>
      </section>
      <section className="login-panel" aria-labelledby="signin-title">
        <div className="login-topline">
          <span className="brand-small">GHL Connect</span>
          <span
            className={`environment-pill ${
              environment === "demo" ? "demo" : ""
            }`}
          >
            {environment === "demo"
              ? "Demo data"
              : environment
                ? `${label(environment)} service`
                : "Connection pending"}
          </span>
        </div>
        <h2 id="signin-title">Sign in to GHL Connect</h2>
        <p className="muted">
          {environment === "demo"
            ? "This fictional dashboard is for team leads, managers and admins."
            : "This dashboard is for approved GHL admins."}{" "}
          Agents use quick tools inside GHL. Call transfer stays in GHL.
        </p>
        {error && <ErrorNotice message={error} onRetry={() => void load()} />}
        {isExtension && error && (
          <p className="field-hint">
            Check the extension's connection status. The company service is
            configured automatically.
          </p>
        )}
        {environment !== "demo" && authConfig && (
          <GhlSignIn config={authConfig} onSuccess={onGhlLogin} />
        )}
        {environment !== "demo" && authConfig?.passwordEnabled && (
          <details className="admin-signin">
            <summary>Administrator fallback sign-in</summary>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void login("/api/auth/login", { email, password });
              }}
            >
              <label>
                Email
                <input
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </label>
              <label>
                Password
                <input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </label>
              <button className="full-button" type="submit" disabled={busy}>
                {busy ? "Signing in…" : "Sign in as administrator"}
              </button>
            </form>
          </details>
        )}
        {environment === "demo" && (
          <div className="demo-login">
            <div className="section-divider">
              <span>Fictional demo people</span>
            </div>
            <p>
              Agent roles open quick tools. Team leads, managers and admins open
              this dashboard. All records are fictional.
            </p>
            <div className="persona-list">
              {personas.map((persona) =>
                canAccessDashboard(persona.role) ? (
                  <button
                    type="button"
                    key={persona.id}
                    disabled={busy}
                    onClick={() =>
                      void login("/api/demo/login", { userId: persona.id })
                    }
                  >
                    <span>
                      <strong>{persona.name}</strong>
                      <small>
                        {persona.team} · {label(persona.role)} · Dashboard
                      </small>
                    </span>
                    <ChevronRight size={18} />
                  </button>
                ) : (
                  <a
                    key={persona.id}
                    href={isExtension ? "./agent.html" : "/ghl-preview.html"}
                  >
                    <span>
                      <strong>{persona.name}</strong>
                      <small>{persona.team} · Agent quick tools</small>
                    </span>
                    <ChevronRight size={18} />
                  </a>
                ),
              )}
            </div>
          </div>
        )}
      </section>
    </main>
  );
}

export function App() {
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [knownDemo, setKnownDemo] = useState(false);
  const [authenticated, setAuthenticated] = useState(
    isExtension || hasWebSession(),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastSync, setLastSync] = useState<number | null>(null);
  const [localNow, setNow] = useState(Date.now());
  const [clockAnchor, setClockAnchor] = useState<number | null>(null);
  const online = useConnectionStatus();
  const [page, setPage] = useState<Page>("team");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [contextOpen, setContextOpen] = useState(false);
  const contextRef = useRef<HTMLElement>(null);
  const contextOrigin = useRef<HTMLElement | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  const [modal, setModal] = useState<Modal>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [flash, setFlash] = useState("");
  const [modalError, setModalError] = useState("");
  const [search, setSearch] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [groupFilter, setGroupFilter] = useState<RosterGroup>("all");
  const [statusFilter, setStatusFilter] = useState("online");
  const [intentFilter, setIntentFilter] = useState("");
  const [eligibleOnly, setEligibleOnly] = useState(false);
  const [queueSearch, setQueueSearch] = useState("");
  const [scriptSearch, setScriptSearch] = useState("");
  const [adminUsers, setAdminUsers] = useState<User[] | null>(null);
  const [adminError, setAdminError] = useState("");
  const authEpoch = useRef(0);
  const viewerId = useRef<string | null>(null);
  const signingOut = useRef(false);
  const mutationInFlight = useRef(false);
  const modalGeneration = useRef(0);
  const closeModal = useCallback(() => {
    modalGeneration.current++;
    setModal(null);
  }, []);
  const mutationRequestVersion = useRef<0 | 1 | null>(null);
  const mutationRevisionVersion = useRef<0 | 1 | null>(null);
  const refreshSequence = useRef(0);
  const refreshInFlight = useRef<{
    epoch: number;
    promise: Promise<boolean>;
  } | null>(null);
  const [documentVisible, setDocumentVisible] = useState(
    () => document.visibilityState !== "hidden",
  );
  const resetAccountUi = useCallback(() => {
    setPage("team");
    setSelectedId(null);
    setContextOpen(false);
    contextOrigin.current = null;
    setNavOpen(false);
    closeModal();
    setActionBusy(false);
    mutationInFlight.current = false;
    mutationRequestVersion.current = null;
    mutationRevisionVersion.current = null;
    setFlash("");
    setModalError("");
    setSearch("");
    setTeamFilter("");
    setGroupFilter("all");
    setStatusFilter("online");
    setIntentFilter("");
    setEligibleOnly(false);
    setQueueSearch("");
    setScriptSearch("");
    setAdminUsers(null);
    setAdminError("");
    setClockAnchor(null);
  }, [closeModal]);
  const refresh = useCallback(
    async (silent = false, force = false) => {
      if (signingOut.current) return false;
      const epoch = authEpoch.current;
      if (refreshInFlight.current?.epoch === epoch) {
        const previous = await refreshInFlight.current.promise;
        if (!force || epoch !== authEpoch.current || signingOut.current)
          return previous;
      }
      if (!silent) setLoading(true);
      const sequence = ++refreshSequence.current;
      const requestStartedAt = Date.now();
      const pending = (async () => {
        try {
          const result = await request<Snapshot>("GET", "/api/snapshot");
          if (
            epoch !== authEpoch.current ||
            sequence !== refreshSequence.current ||
            signingOut.current
          )
            return false;
          const switched = Boolean(
            viewerId.current && viewerId.current !== result.viewer.id,
          );
          if (switched) {
            authEpoch.current++;
            resetAccountUi();
          }
          viewerId.current = result.viewer.id;
          setSnapshot(result);
          setAuthenticated(true);
          setLastSync(Date.now());
          setClockAnchor(requestStartedAt);
          setNow(Date.now());
          setError("");
          if (switched) setLoading(false);
          return true;
        } catch (cause) {
          if (
            epoch !== authEpoch.current ||
            sequence !== refreshSequence.current ||
            signingOut.current
          )
            return false;
          if (cause instanceof ApiError && cause.status === 401) {
            authEpoch.current++;
            viewerId.current = null;
            resetAccountUi();
            clearSession();
            setSnapshot(null);
            setAuthenticated(false);
          } else if (cause instanceof ApiError && cause.status === 403) {
            const deniedEpoch = ++authEpoch.current;
            setSnapshot(null);
            viewerId.current = null;
            resetAccountUi();
            setAccessDenied(true);
            setError("");
            void request<{ environment: "demo" | "local" | "server" }>(
              "GET",
              "/api/health",
            )
              .then((health) => {
                if (deniedEpoch === authEpoch.current && !signingOut.current)
                  setKnownDemo(health.environment === "demo");
              })
              .catch(() => {
                if (deniedEpoch === authEpoch.current && !signingOut.current)
                  setKnownDemo(false);
              });
          } else
            setError(
              cause instanceof Error
                ? cause.message
                : "GHL Connect connection unavailable",
            );
          return false;
        } finally {
          if (
            sequence === refreshSequence.current &&
            epoch === authEpoch.current
          )
            setLoading(false);
        }
      })();
      refreshInFlight.current = { epoch, promise: pending };
      const result = await pending;
      if (refreshInFlight.current?.promise === pending)
        refreshInFlight.current = null;
      return result;
    },
    [resetAccountUi],
  );
  useEffect(() => {
    if (!isExtension) return;
    const onStorageChange = (
      changes: Record<string, chrome.storage.StorageChange>,
      areaName: string,
    ) => {
      if (areaName !== "session" || !changes.token || signingOut.current)
        return;
      authEpoch.current++;
      viewerId.current = null;
      resetAccountUi();
      setSnapshot(null);
      setLastSync(null);
      setAccessDenied(false);
      setKnownDemo(false);
      setError("");
      const hasToken = Boolean(changes.token.newValue);
      setAuthenticated(hasToken);
      setLoading(hasToken);
      if (hasToken && document.visibilityState !== "hidden") void refresh();
    };
    chrome.storage.onChanged.addListener(onStorageChange);
    return () => chrome.storage.onChanged.removeListener(onStorageChange);
  }, [refresh, resetAccountUi]);
  useEffect(() => {
    if (!authenticated) setLoading(false);
  }, [authenticated]);
  useBoundedPoll(
    authenticated && !accessDenied && online && documentVisible,
    () => refresh(snapshot !== null),
    authEpoch.current,
  );
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  useEffect(() => {
    if (!flash) return;
    const id = window.setTimeout(() => setFlash(""), 4500);
    return () => window.clearTimeout(id);
  }, [flash]);
  useEffect(() => {
    if (contextOpen && window.matchMedia("(max-width: 1099px)").matches)
      contextRef.current
        ?.querySelector<HTMLButtonElement>(".context-close")
        ?.focus();
  }, [contextOpen]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || modal) return;
      if (contextOpen) {
        setContextOpen(false);
        window.setTimeout(() => contextOrigin.current?.focus(), 0);
      } else if (navOpen) {
        setNavOpen(false);
        document.querySelector<HTMLButtonElement>(".nav-trigger")?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [contextOpen, navOpen, modal]);
  useEffect(() => {
    const visible = () =>
      setDocumentVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", visible);
    return () => {
      document.removeEventListener("visibilitychange", visible);
    };
  }, []);
  const stale =
    !online ||
    Boolean(error) ||
    !lastSync ||
    localNow < lastSync ||
    localNow - lastSync > 10_000;
  const now = snapshot
    ? serverNow(snapshot.serverTime, clockAnchor, localNow)
    : localNow;
  const mutate = async (
    method: "POST" | "PUT" | "PATCH",
    path: string,
    body: unknown,
    success: string,
  ) => {
    const epoch = authEpoch.current;
    const dialogGeneration = modalGeneration.current;
    if (mutationInFlight.current || signingOut.current) return false;
    if (stale) {
      const message = "GHL Connect is disconnected. Reconnect before saving.";
      setFlash(message);
      setModalError(message);
      return false;
    }
    mutationInFlight.current = true;
    setActionBusy(true);
    setFlash("");
    setModalError("");
    try {
      const result = await request<unknown>(method, path, body);
      if (
        method === "POST" &&
        path === "/api/scripts" &&
        (!result ||
          typeof result !== "object" ||
          !("id" in result) ||
          typeof result.id !== "string" ||
          !result.id)
      )
        throw new ApiError(
          "GHL Connect returned an incomplete save response. Review saved scripts and retry.",
        );
      if (
        epoch !== authEpoch.current ||
        signingOut.current ||
        dialogGeneration !== modalGeneration.current
      )
        return false;
      await refresh(true, true);
      if (
        epoch !== authEpoch.current ||
        signingOut.current ||
        dialogGeneration !== modalGeneration.current
      )
        return false;
      setFlash(success);
      return true;
    } catch (cause) {
      if (
        epoch !== authEpoch.current ||
        signingOut.current ||
        dialogGeneration !== modalGeneration.current
      )
        return false;
      let message =
        cause instanceof Error ? cause.message : "Could not save. Try again.";
      if (
        method === "POST" &&
        path === "/api/scripts" &&
        modal?.kind === "script" &&
        !modal.item &&
        cause instanceof ApiError &&
        (cause.status === 0 || cause.status >= 500)
      ) {
        const draftId = modal.createRequest?.id;
        setModal((current) =>
          current?.kind === "script" && current.createRequest?.id === draftId
            ? {
                ...current,
                createRequest: { ...current.createRequest!, uncertain: true },
              }
            : current,
        );
        message = `Script save could not be confirmed. Review saved scripts before starting another draft. ${message}`;
      }
      if (cause instanceof ApiError && cause.status === 0) setError(message);
      setFlash(message);
      setModalError(message);
      return false;
    } finally {
      if (epoch === authEpoch.current) {
        mutationInFlight.current = false;
        setActionBusy(false);
      }
    }
  };
  const loadMutationCapabilities = async () => {
    if (
      mutationRequestVersion.current !== null &&
      mutationRevisionVersion.current !== null
    )
      return;
    const config = await request<AuthConfig>("GET", "/api/auth/config");
    mutationRequestVersion.current =
      config.mutationRequestVersion === 1 ? 1 : 0;
    mutationRevisionVersion.current =
      config.mutationRevisionVersion === 1 ? 1 : 0;
  };
  const editBody = async (
    body: Record<string, unknown>,
    item: QueueItem | DeskScript,
  ): Promise<Record<string, unknown>> => {
    await loadMutationCapabilities();
    return mutationRevisionVersion.current === 1
      ? { ...body, expectedUpdatedAt: item.updatedAt }
      : body;
  };
  const createBody = async (
    body: Record<string, unknown>,
    draft: CreateRequest | undefined,
  ): Promise<Record<string, unknown>> => {
    await loadMutationCapabilities();
    // Older services reject unknown JSON fields. Use their existing contract
    // only after reading their capabilities, never after a failed config load.
    if (mutationRequestVersion.current === 0) {
      if (draft?.uncertain)
        throw new ApiError(
          "This save could not be confirmed. Review saved items before starting a new draft.",
          409,
        );
      return body;
    }
    const age = draft ? Date.now() - draft.createdAt : Number.POSITIVE_INFINITY;
    if (!draft || age < 0 || age >= 7 * 24 * 60 * 60 * 1000)
      throw new ApiError(
        "This draft has expired. Review saved items before starting a new draft.",
        409,
      );
    return { ...body, requestId: draft.id };
  };
  const logout = async () => {
    signingOut.current = true;
    const epoch = ++authEpoch.current;
    setActionBusy(true);
    try {
      await request("POST", "/api/auth/logout");
    } catch {
      /* Local sign-out still completes. */
    }
    if (epoch !== authEpoch.current) return;
    clearSession();
    viewerId.current = null;
    resetAccountUi();
    setSnapshot(null);
    setAccessDenied(false);
    setKnownDemo(false);
    setAuthenticated(false);
    setLastSync(null);
    setActionBusy(false);
    signingOut.current = false;
  };
  const actTransfer = (
    transfer: Transfer,
    action: "accept" | "decline" | "cancel" | "complete",
  ) =>
    void mutate(
      "POST",
      `/api/transfers/${encodeURIComponent(transfer.id)}/action`,
      { action },
      action === "complete"
        ? "Handoff marked complete in GHL Connect."
        : `Request ${
            action === "accept"
              ? "accepted"
              : action === "decline"
                ? "declined"
                : "cancelled"
          } in GHL Connect.`,
    );
  const loadAdmin = useCallback(async (expectedViewerId: string) => {
    const epoch = authEpoch.current;
    try {
      const result = await request<{ users: User[]; teams: Team[] }>(
        "GET",
        "/api/admin/users",
      );
      if (epoch !== authEpoch.current || viewerId.current !== expectedViewerId)
        return;
      setAdminUsers(result.users);
      setAdminError("");
    } catch (cause) {
      if (epoch !== authEpoch.current || viewerId.current !== expectedViewerId)
        return;
      setAdminError(
        cause instanceof Error ? cause.message : "Could not load users",
      );
    }
  }, []);
  useEffect(() => {
    if (page === "manage" && snapshot?.viewer.role === "admin")
      void loadAdmin(snapshot.viewer.id);
  }, [page, snapshot?.viewer.id, snapshot?.viewer.role, loadAdmin]);
  const login = (result: AuthResult) => {
    signingOut.current = false;
    authEpoch.current++;
    viewerId.current = null;
    resetAccountUi();
    saveSession(result.token);
    setSnapshot(null);
    setAccessDenied(false);
    setKnownDemo(false);
    setLastSync(null);
    setError("");
    setAuthenticated(true);
    setLoading(true);
  };
  const ghlLogin = (_user: User) => {
    signingOut.current = false;
    authEpoch.current++;
    viewerId.current = null;
    resetAccountUi();
    setSnapshot(null);
    setAccessDenied(false);
    setKnownDemo(false);
    setLastSync(null);
    setError("");
    setAuthenticated(true);
    setLoading(true);
  };
  if (!authenticated) return <Login onLogin={login} onGhlLogin={ghlLogin} />;
  if (accessDenied)
    return (
      <main className="access-denied">
        <span className="brand-small">GHL Connect</span>
        <span className="environment-pill">Supervisor dashboard</span>
        <h1>Use your GHL quick tools</h1>
        <p role="alert">
          {knownDemo
            ? "Dashboard access is for fictional team leads, managers and admins."
            : "Dashboard access is for approved GHL admins."}{" "}
          Your agent tools are available inside GHL.
        </p>
        <div className="button-row">
          {knownDemo && !isExtension && (
            <a className="button-link" href="/ghl-preview.html">
              Preview GHL quick tools
            </a>
          )}
          <a
            className="button-link"
            href="https://app.gohighlevel.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Return to GHL
          </a>
          <button
            type="button"
            className="secondary"
            onClick={() => void logout()}
          >
            Sign out
          </button>
        </div>
        {isExtension && (
          <p className="field-hint">
            Open the GHL Connect bar on a GHL tab to use Transfers, My callbacks
            and Scripts.
          </p>
        )}
      </main>
    );
  if (loading && !snapshot)
    return (
      <div className="loading-screen">
        <span className="brand-small">GHL Connect</span>
        <p>Loading GHL Connect…</p>
        <div className="skeleton-line" />
        <div className="skeleton-line" />
        <div className="skeleton-line" />
      </div>
    );
  if (!snapshot)
    return (
      <div className="loading-screen">
        <span className="brand-small">GHL Connect</span>
        <ErrorNotice
          message={error || "Could not load the desk."}
          onRetry={() => void refresh()}
        />
        <button
          type="button"
          className="secondary"
          onClick={() => void logout()}
        >
          Back to sign in
        </button>
      </div>
    );

  const viewer = snapshot.viewer;
  const self = snapshot.agents.find((agent) => agent.id === viewer.id);
  const activityAgents = monitoredAgents(snapshot.agents);
  const selected = activityAgents.find((agent) => agent.id === selectedId);
  const selfMonitored = self?.activityMonitoringEnabled !== false;
  const teamName = (id: string) =>
    snapshot.teams.find((team) => team.id === id)?.name ?? "Team";
  const transferEligible = (agent: AgentView) =>
    canRequestHandoff(agent, snapshot, now, stale);
  const matching = activityAgents
    .filter(
      (agent) =>
        agent.name.toLowerCase().includes(search.trim().toLowerCase()) &&
        (!teamFilter || agent.teamIds.includes(teamFilter)) &&
        (groupFilter === "all" ||
          (groupFilter === "closer"
            ? agent.duty === "closer"
            : agent.bdrMember === true)) &&
        matchesDispatchFilter(agent, statusFilter, snapshot, now, stale) &&
        (!intentFilter || agent.intent === intentFilter) &&
        (!eligibleOnly || transferEligible(agent)),
    )
    .sort((a, b) => compareDispatchAgents(a, b, now, stale, teamName));
  const nextAvailable = matching.find(
    (agent) =>
      effectiveStatus(agent, now, stale) === "available" &&
      statusSeconds(agent.stateSince, now) !== null,
  );
  const exportRoster = () => {
    const csv = rosterCsv(matching, teamName, snapshot, stale, now);
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
    );
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `agent-desk-roster-${new Date(snapshot.serverTime).toISOString().slice(0, 10)}.csv`;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };
  const visibleQueue = snapshot.queueItems
    .filter((item) =>
      `${item.title} ${item.contactName} ${item.phone}`
        .toLowerCase()
        .includes(queueSearch.toLowerCase()),
    )
    .sort((a, b) =>
      a.status === "open" && b.status !== "open"
        ? -1
        : b.status === "open" && a.status !== "open"
          ? 1
          : (a.dueAt ?? Infinity) - (b.dueAt ?? Infinity),
    );
  const nextCallback = snapshot.queueItems
    .filter(
      (item) =>
        item.ownerId === viewer.id &&
        item.status === "open" &&
        item.dueAt !== null,
    )
    .sort((a, b) => (a.dueAt ?? 0) - (b.dueAt ?? 0))[0];
  const visibleScripts = snapshot.scripts
    .filter((item) =>
      `${item.title} ${item.content}`
        .toLowerCase()
        .includes(scriptSearch.toLowerCase()),
    )
    .sort((a, b) => a.title.localeCompare(b.title));
  const transfers = snapshot.transfers
    .filter(
      (transfer) =>
        transfer.fromUserId === viewer.id || transfer.toUserId === viewer.id,
    )
    .sort(
      (a, b) =>
        Number(isActiveTransfer(b, now)) - Number(isActiveTransfer(a, now)) ||
        b.updatedAt - a.updatedAt ||
        a.id.localeCompare(b.id),
    );
  const activeTransfers = transfers.filter((transfer) =>
    isActiveTransfer(transfer, now),
  );
  const currentTransfer = activeTransfers[0] ?? transfers[0];
  let transferHistoryCount = 0;
  const otherRequests = transfers.filter(
    (transfer) =>
      transfer.id !== currentTransfer?.id &&
      (isActiveTransfer(transfer, now) || transferHistoryCount++ < 7),
  );
  const manager = ["manager", "admin"].includes(viewer.role);
  const canEditScripts = canCreateScript(viewer);
  const openModal = (next: Exclude<Modal, null>) => {
    modalGeneration.current++;
    setModalError("");
    setModal(
      (next.kind === "queue" || next.kind === "script") && !next.item
        ? {
            ...next,
            createRequest: { id: crypto.randomUUID(), createdAt: Date.now() },
          }
        : next,
    );
  };
  const openContext = () => {
    contextOrigin.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    setContextOpen(true);
  };
  const closeContext = () => {
    setContextOpen(false);
    window.setTimeout(() => contextOrigin.current?.focus(), 0);
  };
  const openPage = (next: Page) => {
    setPage(next);
    setNavOpen(false);
    setContextOpen(false);
  };
  return (
    <div className="app-shell">
      <aside
        className={`nav-rail ${navOpen ? "nav-open" : ""}`}
        aria-label="Main navigation"
      >
        <div className="brand">
          <span className="brand-number">GC</span>
          <span>
            GHL
            <br />
            Connect
          </span>
        </div>
        <nav aria-label="GHL Connect sections">
          <button
            type="button"
            className={page === "team" ? "active" : ""}
            onClick={() => openPage("team")}
          >
            <Users size={18} />
            Team
          </button>
          {viewer.role === "admin" && snapshot.environment !== "demo" && (
            <button
              type="button"
              className={page === "analytics" ? "active" : ""}
              onClick={() => openPage("analytics")}
            >
              <ChartColumnIncreasing size={18} />
              Analytics
            </button>
          )}
          {manager && (
            <button
              type="button"
              className={page === "manage" ? "active" : ""}
              onClick={() => openPage("manage")}
            >
              <Settings2 size={18} />
              Manage team
            </button>
          )}
        </nav>
        <div className="nav-foot">
          <div className="account-card">
            <strong>{viewer.name}</strong>
            <span>
              {label(viewer.role)} · {label(viewer.duty)}
            </span>
          </div>
          <button
            type="button"
            className="logout-button"
            onClick={() => void logout()}
          >
            <LogOut size={17} />
            Sign out
          </button>
        </div>
      </aside>
      <div className="main-wrap">
        <header className="topbar">
          <div className="top-left">
            <button
              type="button"
              className="icon-button nav-trigger"
              aria-label="Open menu"
              aria-expanded={navOpen}
              onClick={() => setNavOpen(!navOpen)}
            >
              <Menu size={21} />
            </button>
            <div className="top-brand">GHL Connect</div>
            <span
              className={`environment-pill ${
                snapshot.environment === "demo" ? "demo" : ""
              }`}
            >
              {snapshot.environment === "demo"
                ? "Demo data"
                : `${label(snapshot.environment)} service`}
            </span>
          </div>
          <div className="top-right">
            <span className={`connection ${stale ? "disconnected" : ""}`}>
              <span className="connection-dot" />
              {stale ? "GHL Connect disconnected" : "Connected to GHL Connect"}
            </span>
            <span className="top-location">
              {viewer.teamIds.map(teamName).join(", ") || "No team assigned"}
            </span>
            <button
              type="button"
              className="icon-button context-trigger"
              aria-label="Open my status and details"
              onClick={() => openContext()}
            >
              <Settings2 size={20} />
            </button>
          </div>
        </header>
        {stale && (
          <div className="stale-banner" role="alert">
            <CircleAlert size={18} />
            <span>
              GHL Connect updates are delayed. Last sync{" "}
              {age(lastSync, localNow)}. Transfer actions are paused until
              refreshed.
            </span>
            <button type="button" onClick={() => void refresh(true)}>
              <RefreshCw size={15} />
              Retry
            </button>
          </div>
        )}
        {!stale &&
          activityAgents.some((agent) =>
            ["unknown", "offline"].includes(effectiveStatus(agent, now)),
          ) && (
            <div className="observation-banner">
              GHL activity may be unavailable for some agents. Check each row’s
              observation before a handoff.
            </div>
          )}
        {flash && (
          <div
            className={`flash ${
              /could not|failed|unavailable|error|expired|already|invalid/i.test(
                flash,
              )
                ? "flash-error"
                : ""
            }`}
            role="status"
          >
            {flash}
            <button
              type="button"
              aria-label="Dismiss message"
              onClick={() => setFlash("")}
            >
              <X size={16} />
            </button>
          </div>
        )}
        <div className="workspace">
          <main className="primary-pane" id="main-content">
            {page === "analytics" &&
              viewer.role === "admin" &&
              snapshot.environment !== "demo" && <Analytics key={viewer.id} />}
            {page === "team" && (
              <>
                <div className="page-heading">
                  <div>
                    <p className="eyebrow">DISPATCH / TEAM</p>
                    <h1>Your dispatch floor</h1>
                    <p className="muted result-count" aria-live="polite">
                      See who can take the next handoff, and who needs time or a
                      fresh signal.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="my-status-button"
                    onClick={() => {
                      setSelectedId(null);
                      openContext();
                    }}
                  >
                    {selfMonitored ? "My status" : "My account"}{" "}
                    <ChevronRight size={17} />
                  </button>
                </div>
                <DashboardOverview
                  snapshot={snapshot}
                  now={now}
                  stale={stale}
                  activeFilter={eligibleOnly ? "ready" : statusFilter}
                  teamFilter={teamFilter}
                  onTeam={setTeamFilter}
                  onActivity={(status) => {
                    setStatusFilter(status);
                    setEligibleOnly(false);
                  }}
                  onDueCallbacks={() => openPage("queue")}
                />
                <div className="roster-toolbar">
                  <button
                    type="button"
                    className="secondary"
                    onClick={exportRoster}
                  >
                    Export visible CSV
                  </button>
                  <label className="search-field">
                    <Search size={18} />
                    <span className="sr-only">Search agents by name</span>
                    <input
                      type="search"
                      placeholder="Find an agent"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                    />
                  </label>
                  <label className="filter-select">
                    <span className="sr-only">Filter by team</span>
                    <select
                      value={teamFilter}
                      onChange={(event) => setTeamFilter(event.target.value)}
                    >
                      <option value="">All teams</option>
                      {snapshot.teams.map((team) => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="filter-select">
                    <span className="sr-only">Filter by group</span>
                    <select
                      value={groupFilter}
                      onChange={(event) =>
                        setGroupFilter(event.target.value as RosterGroup)
                      }
                    >
                      <option value="all">All agents</option>
                      <option value="closer">Closers</option>
                      <option value="bdr">BDR</option>
                    </select>
                  </label>
                  <label className="filter-select">
                    <span className="sr-only">Filter by activity</span>
                    <select
                      value={statusFilter}
                      onChange={(event) => setStatusFilter(event.target.value)}
                    >
                      <option value="">All activity</option>
                      <option value="online">Online people</option>
                      <option value="ready">Ready recipients</option>
                      <option value="away">Away</option>
                      <option value="unknown_offline">Unknown / offline</option>
                      {statusOptions.map((status) => (
                        <option key={status} value={status}>
                          {STATUS_LABELS[status]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="filter-select">
                    <span className="sr-only">Filter by intent</span>
                    <select
                      value={intentFilter}
                      onChange={(event) => setIntentFilter(event.target.value)}
                    >
                      <option value="">All intents</option>
                      {intents.map((intent) => (
                        <option key={intent} value={intent}>
                          {INTENT_LABELS[intent]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="check-filter">
                    <input
                      type="checkbox"
                      checked={eligibleOnly}
                      onChange={(event) =>
                        setEligibleOnly(event.target.checked)
                      }
                    />
                    Ready to request
                  </label>
                </div>
                {(teamFilter ||
                  groupFilter !== "all" ||
                  statusFilter ||
                  intentFilter ||
                  eligibleOnly) && (
                  <div className="filter-chips" aria-label="Active filters">
                    {teamFilter && (
                      <button type="button" onClick={() => setTeamFilter("")}>
                        {teamName(teamFilter)} <X size={14} />
                      </button>
                    )}
                    {groupFilter !== "all" && (
                      <button
                        type="button"
                        onClick={() => setGroupFilter("all")}
                      >
                        {groupFilter === "closer" ? "Closers" : "BDR"}{" "}
                        <X size={14} />
                      </button>
                    )}
                    {statusFilter && (
                      <button type="button" onClick={() => setStatusFilter("")}>
                        {statusFilter === "unknown_offline"
                          ? "Unknown / offline"
                          : statusFilter === "online"
                            ? "Online people"
                            : statusFilter === "ready"
                              ? "Ready recipients"
                              : statusFilter === "away"
                                ? "Away"
                                : STATUS_LABELS[statusFilter as Status]}{" "}
                        <X size={14} />
                      </button>
                    )}
                    {intentFilter && (
                      <button type="button" onClick={() => setIntentFilter("")}>
                        {INTENT_LABELS[intentFilter as Intent]} <X size={14} />
                      </button>
                    )}
                    {eligibleOnly && (
                      <button
                        type="button"
                        onClick={() => setEligibleOnly(false)}
                      >
                        Ready to request <X size={14} />
                      </button>
                    )}
                  </div>
                )}
                <div className="roster-section-heading">
                  <h2>Activity roster</h2>
                  <span className="meta" aria-live="polite">
                    {matching.length} of {activityAgents.length} shown ·
                    available first, longest waiting first
                  </span>
                </div>
                {!stale && nextAvailable && (
                  <p
                    className="next-available"
                    aria-label={`Longest available in filtered roster: ${nextAvailable.name}`}
                  >
                    <strong>Longest available</strong>
                    <span>{nextAvailable.name}</span>
                    <small>
                      Suggestion from the current filters · handoff eligibility
                      is checked separately
                    </small>
                  </p>
                )}
                <div className="roster-head">
                  <span>AGENT / TEAM</span>
                  <span>ACTIVITY / INTENT</span>
                  <span>TIME IN STATUS</span>
                  <span>HANDOFF</span>
                </div>
                <div
                  className="roster-list"
                  role="list"
                  aria-label="Team roster"
                >
                  {matching.map((agent) => (
                    <ManagerRosterRow
                      key={agent.id}
                      agent={agent}
                      snapshot={snapshot}
                      now={now}
                      stale={stale}
                      selected={selectedId === agent.id}
                      busy={actionBusy}
                      teamName={teamName}
                      onSelect={() => {
                        if (selectedId !== agent.id && contextRef.current)
                          contextRef.current.scrollTop = 0;
                        setSelectedId(agent.id);
                        openContext();
                      }}
                      onTransfer={() => openModal({ kind: "transfer", agent })}
                    />
                  ))}
                  {matching.length === 0 && (
                    <div className="empty-state">
                      <Search size={22} />
                      <strong>
                        {activityAgents.length === 0
                          ? "No monitored agents in your scope yet."
                          : "No agents match these filters."}
                      </strong>
                      <p>
                        {activityAgents.length === 0
                          ? "There is no monitored activity in your current scope."
                          : stale
                            ? "Live readiness is paused while disconnected. Clear activity filters to see the last received roster."
                            : "Clear a filter or try another name."}
                      </p>
                      <button
                        type="button"
                        className="secondary"
                        onClick={() => {
                          setSearch("");
                          setTeamFilter("");
                          setGroupFilter("all");
                          setStatusFilter("");
                          setIntentFilter("");
                          setEligibleOnly(false);
                        }}
                      >
                        Clear filters
                      </button>
                    </div>
                  )}
                </div>
                <p className="footnote dispatch-footnote">
                  Ready means an eligible agent with a fresh GHL idle
                  observation and Ready intent. Call sessions include ringing;
                  answered state is unverified. All times describe observed
                  status, not call duration.
                </p>
              </>
            )}
            {page === "queue" && (
              <>
                <div className="page-heading">
                  <div>
                    <p className="eyebrow">FOLLOW-UP / GHL CONNECT</p>
                    <h1>Callbacks & tasks</h1>
                    <p className="muted">
                      Tasks and outcomes are saved to GHL Connect, not GHL.
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={stale}
                    onClick={() => openModal({ kind: "queue" })}
                  >
                    <Plus size={18} />
                    New task
                  </button>
                </div>
                <label className="search-field standalone">
                  <Search size={18} />
                  <span className="sr-only">Search queue</span>
                  <input
                    type="search"
                    placeholder="Search tasks, contacts or phone"
                    value={queueSearch}
                    onChange={(event) => setQueueSearch(event.target.value)}
                  />
                </label>
                <div className="queue-list">
                  {visibleQueue.map((item) => (
                    <article
                      key={item.id}
                      className={`queue-row ${taskDue(item, now) ? "due" : ""}`}
                    >
                      <div className="queue-main">
                        <span className="queue-status">
                          {item.status === "open"
                            ? taskDue(item, now)
                              ? "Due"
                              : "Open"
                            : item.status === "done"
                              ? "Done"
                              : "Skipped"}
                        </span>
                        <div>
                          <h2>{item.title}</h2>
                          <p>
                            {item.contactName || "No contact name"}
                            {item.phone ? ` · ${item.phone}` : ""}
                            {item.ownerId !== viewer.id
                              ? ` · Owner: ${nameFor(snapshot, item.ownerId)}`
                              : ""}
                          </p>
                          <small>
                            {item.dueAt
                              ? `Due ${dateTime(item.dueAt)}`
                              : "No due time"}
                            {item.disposition
                              ? ` · ${DISPOSITION_LABELS[item.disposition]}`
                              : ""}
                          </small>
                        </div>
                      </div>
                      <div className="button-row">
                        <button
                          type="button"
                          className="secondary"
                          disabled={item.status !== "open"}
                          title={
                            item.status !== "open"
                              ? "Reopen to edit"
                              : undefined
                          }
                          onClick={() => openModal({ kind: "queue", item })}
                        >
                          Edit
                        </button>
                        {item.status === "open" ? (
                          <button
                            type="button"
                            className="secondary"
                            disabled={actionBusy || stale}
                            onClick={() =>
                              void mutate(
                                "POST",
                                `/api/queue/${encodeURIComponent(
                                  item.id,
                                )}/action`,
                                { action: "skip" },
                                "Task skipped in GHL Connect.",
                              )
                            }
                          >
                            Skip
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="secondary"
                            disabled={actionBusy || stale}
                            onClick={() =>
                              void mutate(
                                "POST",
                                `/api/queue/${encodeURIComponent(
                                  item.id,
                                )}/action`,
                                { action: "reopen" },
                                "Task reopened in GHL Connect.",
                              )
                            }
                          >
                            Reopen
                          </button>
                        )}
                      </div>
                    </article>
                  ))}
                  {visibleQueue.length === 0 && (
                    <div className="empty-state">
                      <Clock3 size={22} />
                      <strong>
                        {queueSearch
                          ? "No matching tasks."
                          : "No callbacks due."}
                      </strong>
                      <p>
                        {queueSearch
                          ? "Try another search."
                          : "Your next saved task will appear here."}
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
            {page === "scripts" && (
              <>
                <div className="page-heading">
                  <div>
                    <p className="eyebrow">REFERENCE / GHL CONNECT</p>
                    <h1>Scripts</h1>
                    <p className="muted">
                      Copy plain text to use where you need it.
                    </p>
                  </div>
                  {canEditScripts && (
                    <button
                      type="button"
                      disabled={stale}
                      onClick={() => openModal({ kind: "script" })}
                    >
                      <Plus size={18} />
                      New script
                    </button>
                  )}
                </div>
                <label className="search-field standalone">
                  <Search size={18} />
                  <span className="sr-only">Search scripts</span>
                  <input
                    type="search"
                    placeholder="Search title or text"
                    value={scriptSearch}
                    onChange={(event) => setScriptSearch(event.target.value)}
                  />
                </label>
                <div className="script-list">
                  {visibleScripts.map((item) => (
                    <article key={item.id} className="script-card">
                      <div className="row-between">
                        <div>
                          <h2>{item.title}</h2>
                          <p className="meta">
                            {item.teamIds.length
                              ? item.teamIds.map(teamName).join(", ")
                              : "All authorized teams"}{" "}
                            · Updated {dateTime(item.updatedAt)}
                          </p>
                        </div>
                        <div className="button-row">
                          <button
                            type="button"
                            className="secondary"
                            onClick={() => {
                              const epoch = authEpoch.current;
                              void navigator.clipboard
                                .writeText(item.content)
                                .then(() => {
                                  if (epoch === authEpoch.current)
                                    setFlash("Script copied to clipboard.");
                                })
                                .catch(() => {
                                  if (epoch === authEpoch.current)
                                    setFlash("Could not copy script.");
                                });
                            }}
                          >
                            <Clipboard size={16} />
                            Copy
                          </button>
                          {canEditScript(viewer, item) && (
                            <button
                              type="button"
                              className="secondary"
                              onClick={() =>
                                openModal({ kind: "script", item })
                              }
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="script-body">{item.content}</div>
                    </article>
                  ))}
                  {visibleScripts.length === 0 && (
                    <div className="empty-state">
                      <Clipboard size={22} />
                      <strong>No scripts found.</strong>
                      <p>
                        {scriptSearch
                          ? "Try another search."
                          : "A manager can add scripts for your team."}
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
            {page === "manage" && manager && (
              <>
                <div className="page-heading">
                  <div>
                    <p className="eyebrow">ADMINISTRATION</p>
                    <h1>Manage team</h1>
                    <p className="muted">
                      {viewer.role === "admin"
                        ? "Accounts and team access are enforced by GHL Connect."
                        : "Review activity and maintain shared scripts."}
                    </p>
                  </div>
                </div>
                {viewer.role === "admin" ? (
                  <>
                    <AdminSetup key={viewer.id} viewerId={viewer.id} />
                    <div className="management-head">
                      <h2>People</h2>
                      <button
                        type="button"
                        disabled={stale}
                        onClick={() => openModal({ kind: "user" })}
                      >
                        <Plus size={17} />
                        Add person
                      </button>
                    </div>
                    {adminError && (
                      <ErrorNotice
                        message={adminError}
                        onRetry={() => void loadAdmin(viewer.id)}
                      />
                    )}
                    {adminUsers ? (
                      <div
                        className="management-table"
                        role="table"
                        aria-label="Users"
                      >
                        <div
                          className="management-row management-heading"
                          role="row"
                        >
                          <span role="columnheader">Person</span>
                          <span role="columnheader">Role / duty</span>
                          <span role="columnheader">Teams</span>
                          <span role="columnheader">State</span>
                          <span role="columnheader">Action</span>
                        </div>
                        {adminUsers.map((user) => (
                          <div
                            className="management-row"
                            role="row"
                            key={user.id}
                          >
                            <span role="cell">
                              <strong>{user.name}</strong>
                              <small>{user.email}</small>
                            </span>
                            <span role="cell">
                              {label(user.role)} · {label(user.duty)}
                            </span>
                            <span role="cell">
                              {user.teamIds.map(teamName).join(", ") ||
                                "No team"}
                            </span>
                            <span role="cell">
                              {user.active ? "Active" : "Inactive"}
                            </span>
                            <span role="cell">
                              <button
                                type="button"
                                className="secondary"
                                onClick={() =>
                                  openModal({ kind: "user", user })
                                }
                              >
                                Edit
                              </button>
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="muted">Loading people…</p>
                    )}
                    <div className="management-head team-management">
                      <h2>Teams</h2>
                      <button
                        type="button"
                        className="secondary"
                        disabled={stale}
                        onClick={() => openModal({ kind: "team" })}
                      >
                        <Plus size={17} />
                        Add team
                      </button>
                    </div>
                    <div className="team-list">
                      {snapshot.teams.map((team) => (
                        <div className="team-entry" key={team.id}>
                          <span
                            className="team-color"
                            style={{ background: team.color }}
                          />
                          <span className="team-entry-name">
                            <strong>{team.name}</strong>
                            <small>
                              {team.pilotEnabled
                                ? "Pilot enabled"
                                : "Pilot off"}
                            </small>
                          </span>
                          <button
                            type="button"
                            className="secondary"
                            disabled={stale}
                            onClick={() => openModal({ kind: "team", team })}
                          >
                            Edit
                          </button>
                        </div>
                      ))}
                      {snapshot.teams.length === 0 && (
                        <p className="muted">
                          No local teams yet. Add a team to organize access.
                        </p>
                      )}
                    </div>
                    <AdminDirectory
                      key={viewer.id}
                      teams={snapshot.teams}
                      users={adminUsers ?? []}
                      disabled={stale}
                      onProvisioned={async () => {
                        await loadAdmin(viewer.id);
                        await refresh(true, true);
                      }}
                    />
                  </>
                ) : (
                  <div className="empty-state">
                    <ShieldCheck size={22} />
                    <strong>Team management is limited to admins.</strong>
                    <p>
                      Managers can view their assigned teams and edit scripts.
                      Ask an admin to change accounts or teams.
                    </p>
                  </div>
                )}
              </>
            )}
          </main>
          <aside
            ref={contextRef}
            className={`context-pane ${contextOpen ? "context-open" : ""}`}
            aria-label="Status and details"
          >
            <div className="context-heading">
              <span>{selected ? "DISPATCH CONTEXT" : "YOUR DESK"}</span>
              <button
                type="button"
                className="icon-button context-close"
                aria-label="Close status panel"
                onClick={closeContext}
              >
                <X size={20} />
              </button>
            </div>
            {selected && (
              <AgentDispatchDetail
                agent={selected}
                snapshot={snapshot}
                now={now}
                stale={stale}
                busy={actionBusy}
                teamName={teamName}
                onClose={() => setSelectedId(null)}
                onTransfer={() =>
                  openModal({ kind: "transfer", agent: selected })
                }
                onQueueItem={(item) => {
                  openPage("queue");
                  openModal({ kind: "queue", item });
                }}
              />
            )}
            <section className="context-section self-status">
              <h2>{selfMonitored ? "My status" : "My account"}</h2>
              <p className="self-name">{viewer.name}</p>
              {selfMonitored && (
                <>
                  <div className="self-observed">
                    <StatusMark
                      status={
                        self ? effectiveStatus(self, now, stale) : "unknown"
                      }
                    />
                    <span>
                      Intent: {self ? INTENT_LABELS[self.intent] : "Unknown"}
                    </span>
                  </div>
                  <p className="meta">
                    {self
                      ? presenceDescription(self, now, stale)
                      : "GHL tab not observed"}
                  </p>
                  {self && effectiveStatus(self, now, stale) === "busy" && (
                    <p className="field-hint">
                      Ringing or active; answered state is unverified.
                    </p>
                  )}
                  {self && effectiveStatus(self, now, stale) === "wrap_up" && (
                    <p className="field-hint">
                      Wrap-up clears after GHL reports a fresh idle screen.
                      Finish it there.
                    </p>
                  )}
                  <IntentControl
                    key={`${viewer.id}:${self?.intent}`}
                    intent={self?.intent ?? "ready"}
                    disabled={actionBusy || stale}
                    onSave={(intent) =>
                      mutate(
                        "PUT",
                        "/api/me/intent",
                        { intent },
                        "Status intent saved to GHL Connect.",
                      )
                    }
                  />
                </>
              )}
            </section>

            <section className="context-section">
              <div className="row-between">
                <h2>Transfer inbox</h2>
                <span className="count-pill">{activeTransfers.length}</span>
              </div>
              {currentTransfer ? (
                <TransferActions
                  transfer={currentTransfer}
                  snapshot={snapshot}
                  now={now}
                  stale={stale}
                  busy={actionBusy}
                  act={actTransfer}
                />
              ) : (
                <p className="quiet-empty">
                  No transfer requests. When a teammate sends one, it appears
                  here.
                </p>
              )}
              {otherRequests.length > 0 && (
                <details className="transfer-history">
                  <summary>Other requests ({otherRequests.length})</summary>
                  {otherRequests.map((transfer) => (
                    <TransferActions
                      key={transfer.id}
                      transfer={transfer}
                      snapshot={snapshot}
                      now={now}
                      stale={stale}
                      busy={actionBusy}
                      act={actTransfer}
                    />
                  ))}
                </details>
              )}
            </section>
            <section className="context-section">
              <h2>Next callback</h2>
              {nextCallback ? (
                <div className="next-callback">
                  <strong>{nextCallback.title}</strong>
                  <p>{nextCallback.contactName || "No contact name"}</p>
                  <span className="meta">{dateTime(nextCallback.dueAt)}</span>
                  <button
                    type="button"
                    className="text-button"
                    onClick={() => {
                      openPage("queue");
                      openModal({ kind: "queue", item: nextCallback });
                    }}
                  >
                    Open task <ChevronRight size={16} />
                  </button>
                </div>
              ) : (
                <p className="quiet-empty">
                  No callbacks due. Your next saved task will appear here.
                </p>
              )}
            </section>
            {viewer.role === "admin" && snapshot.environment !== "demo" && (
              <ActiveCallProbeStatus key={viewer.id} />
            )}
          </aside>
        </div>
      </div>
      {modal?.kind === "transfer" && (
        <TransferModal
          agent={modal.agent}
          onClose={closeModal}
          onSubmit={async (summary) => {
            const currentTarget = snapshot.agents.find(
              (agent) => agent.id === modal.agent.id,
            );
            if (!currentTarget || !transferEligible(currentTarget)) {
              setModalError(
                "This recipient is no longer ready. Refresh and choose a ready recipient.",
              );
              return false;
            }
            const epoch = authEpoch.current;
            const ok = await mutate(
              "POST",
              "/api/transfers",
              { toUserId: modal.agent.id, summary },
              "Transfer request sent in GHL Connect. Complete the call transfer in GHL after acceptance.",
            );
            if (ok && epoch === authEpoch.current) closeModal();
            return ok;
          }}
          sending={actionBusy}
          busy={
            actionBusy ||
            stale ||
            !snapshot.agents.some(
              (agent) => agent.id === modal.agent.id && transferEligible(agent),
            )
          }
          saveError={
            modalError ||
            (!snapshot.agents.some(
              (agent) => agent.id === modal.agent.id && transferEligible(agent),
            )
              ? "This recipient is no longer ready. Close this request and choose a ready recipient."
              : "")
          }
        />
      )}
      {modal?.kind === "queue" && (
        <QueueModal
          item={modal.item}
          users={snapshot.agents
            .filter(
              (agent) =>
                viewer.role === "admin" ||
                viewer.role === "manager" ||
                (viewer.role === "lead" &&
                  agent.teamIds.some((id) => viewer.teamIds.includes(id))) ||
                agent.id === viewer.id,
            )
            .map((agent) => ({ id: agent.id, name: agent.name }))}
          viewer={viewer}
          onClose={closeModal}
          onSave={async (body, complete) => {
            const epoch = authEpoch.current;
            const dialogGeneration = modalGeneration.current;
            const method = modal.item ? "PUT" : "POST";
            const path = modal.item
              ? `/api/queue/${encodeURIComponent(modal.item.id)}`
              : "/api/queue";
            if (stale) {
              const message =
                "GHL Connect is disconnected. Reconnect before saving.";
              setFlash(message);
              setModalError(message);
              return false;
            }
            if (mutationInFlight.current || signingOut.current) return false;
            mutationInFlight.current = true;
            setActionBusy(true);
            setFlash("");
            setModalError("");
            let detailsSaved = false;
            try {
              const payload = modal.item
                ? await editBody(body, modal.item)
                : await createBody(body, modal.createRequest);
              if (
                epoch !== authEpoch.current ||
                signingOut.current ||
                dialogGeneration !== modalGeneration.current
              )
                return false;
              const saved = await request<QueueItem>(method, path, payload);
              if (!saved || typeof saved.id !== "string" || !saved.id)
                throw new ApiError(
                  "GHL Connect returned an incomplete save response. Review saved tasks and retry.",
                );
              if (
                epoch !== authEpoch.current ||
                signingOut.current ||
                dialogGeneration !== modalGeneration.current
              )
                return false;
              detailsSaved = true;
              if (!modal.item) setModal({ kind: "queue", item: saved });
              if (complete)
                await request(
                  "POST",
                  `/api/queue/${encodeURIComponent(saved.id)}/action`,
                  {
                    action: "complete",
                    disposition: complete.disposition,
                    notes: body.notes,
                    dueAt: body.dueAt,
                  },
                );
              if (
                epoch !== authEpoch.current ||
                signingOut.current ||
                dialogGeneration !== modalGeneration.current
              )
                return false;
              await refresh(true, true);
              if (
                epoch !== authEpoch.current ||
                signingOut.current ||
                dialogGeneration !== modalGeneration.current
              )
                return false;
              setFlash("Task saved to GHL Connect.");
              closeModal();
              return true;
            } catch (cause) {
              if (
                epoch !== authEpoch.current ||
                signingOut.current ||
                dialogGeneration !== modalGeneration.current
              )
                return false;
              const uncertain =
                !detailsSaved &&
                !modal.item &&
                cause instanceof ApiError &&
                (cause.status === 0 || cause.status >= 500);
              if (uncertain) {
                const draftId = modal.createRequest?.id;
                setModal((current) =>
                  current?.kind === "queue" &&
                  current.createRequest?.id === draftId
                    ? {
                        ...current,
                        createRequest: {
                          ...current.createRequest!,
                          uncertain: true,
                        },
                      }
                    : current,
                );
              }
              const message = `${
                detailsSaved
                  ? cause instanceof ApiError &&
                    (cause.status === 0 || cause.status >= 500)
                    ? "Task details saved. Completion could not be confirmed; review the saved task status. "
                    : "Task details saved, but completion failed. "
                  : uncertain
                    ? "Task save could not be confirmed. Review saved tasks before starting another draft. "
                    : "Could not save task. "
              }${cause instanceof Error ? cause.message : "Try again."}`;
              if (cause instanceof ApiError && cause.status === 0)
                setError(cause.message);
              setFlash(message);
              setModalError(message);
              return false;
            } finally {
              if (epoch === authEpoch.current) {
                mutationInFlight.current = false;
                setActionBusy(false);
              }
            }
          }}
          busy={actionBusy || stale}
          saveError={modalError}
        />
      )}
      {modal?.kind === "script" && (
        <ScriptModal
          item={modal.item}
          viewer={viewer}
          teams={
            viewer.role === "admin"
              ? snapshot.teams
              : snapshot.teams.filter((team) =>
                  viewer.teamIds.includes(team.id),
                )
          }
          onClose={closeModal}
          onSave={async (body) => {
            if (
              !canCreateScript(viewer) ||
              (modal.item && !canEditScript(viewer, modal.item)) ||
              !canEditScript(viewer, { teamIds: body.teamIds })
            ) {
              setModalError("Choose at least one team you manage.");
              return false;
            }
            const epoch = authEpoch.current;
            const dialogGeneration = modalGeneration.current;
            let payload: Record<string, unknown>;
            try {
              payload = modal.item
                ? await editBody(body, modal.item)
                : await createBody(body, modal.createRequest);
            } catch (cause) {
              if (
                epoch !== authEpoch.current ||
                signingOut.current ||
                dialogGeneration !== modalGeneration.current
              )
                return false;
              setModalError(
                cause instanceof Error
                  ? cause.message
                  : "Could not save script. Try again.",
              );
              return false;
            }
            if (
              epoch !== authEpoch.current ||
              signingOut.current ||
              dialogGeneration !== modalGeneration.current
            )
              return false;
            const ok = await mutate(
              modal.item ? "PUT" : "POST",
              modal.item
                ? `/api/scripts/${encodeURIComponent(modal.item.id)}`
                : "/api/scripts",
              payload,
              "Script saved to GHL Connect.",
            );
            if (ok && epoch === authEpoch.current) closeModal();
            return ok;
          }}
          busy={actionBusy || stale}
          saveError={modalError}
        />
      )}
      {modal?.kind === "user" && (
        <UserModal
          user={modal.user}
          teams={snapshot.teams}
          onClose={closeModal}
          onSave={async (body) => {
            const epoch = authEpoch.current;
            const ok = await mutate(
              modal.user ? "PATCH" : "POST",
              modal.user
                ? `/api/admin/users/${encodeURIComponent(modal.user.id)}`
                : "/api/admin/users",
              body,
              "User saved to GHL Connect.",
            );
            if (ok && epoch === authEpoch.current) {
              closeModal();
              await loadAdmin(viewer.id);
            }
            return ok;
          }}
          busy={actionBusy || stale}
          saveError={modalError}
        />
      )}
      {modal?.kind === "team" && (
        <TeamModal
          team={modal.team}
          onClose={closeModal}
          onSave={async (body) => {
            const epoch = authEpoch.current;
            const ok = await mutate(
              modal.team ? "PATCH" : "POST",
              modal.team
                ? `/api/admin/teams/${encodeURIComponent(modal.team.id)}`
                : "/api/admin/teams",
              body,
              "Team saved to GHL Connect.",
            );
            if (ok && epoch === authEpoch.current) {
              closeModal();
              await loadAdmin(viewer.id);
            }
            return ok;
          }}
          busy={actionBusy || stale}
          saveError={modalError}
        />
      )}
    </div>
  );
}

function IntentControl({
  intent,
  disabled,
  onSave,
}: {
  intent: Intent;
  disabled: boolean;
  onSave: (intent: Intent) => Promise<boolean>;
}) {
  const [choice, setChoice] = useState<Intent>(intent);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const save = async () => {
    setSaving(true);
    setError("");
    const ok = await onSave(choice);
    if (!ok)
      setError(
        "Could not save status. Your previous intent is still shown above. Retry when connected.",
      );
    setSaving(false);
  };
  return (
    <div className="intent-control">
      <label htmlFor="intent-choice">Availability intent</label>
      <div className="intent-row">
        <select
          id="intent-choice"
          value={choice}
          onChange={(event) => setChoice(event.target.value as Intent)}
          disabled={disabled || saving}
        >
          {intents.map((value) => (
            <option key={value} value={value}>
              {INTENT_LABELS[value]}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={disabled || saving || choice === intent}
          onClick={() => void save()}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      <p className="field-hint">
        Ready requires a fresh idle observation from the GHL extension.
      </p>
    </div>
  );
}

function TransferModal({
  agent,
  onClose,
  onSubmit,
  busy,
  saveError,
  sending,
}: {
  agent: AgentView;
  onClose: () => void;
  onSubmit: (summary: string) => Promise<boolean>;
  busy: boolean;
  sending: boolean;
  saveError: string;
}) {
  const [summary, setSummary] = useState("");
  const [error, setError] = useState("");
  return (
    <ModalShell
      title={`Request transfer to ${agent.name}`}
      error={saveError}
      description="Coordinate here, then complete the call transfer in GHL."
      onClose={onClose}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (busy) return;
          if (!summary.trim()) {
            setError("Add a short contact reference or context.");
            return;
          }
          void onSubmit(summary.trim());
        }}
      >
        <label>
          Contact reference and context
          <textarea
            value={summary}
            maxLength={500}
            required
            rows={4}
            placeholder="Contact name or reference, reason for handoff, next step"
            onChange={(event) => {
              setSummary(event.target.value);
              setError("");
            }}
            autoFocus
          />
        </label>
        {error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}
        <p className="field-hint">
          Saved as a GHL Connect request only. No GHL call is placed or
          transferred.
        </p>
        <div className="dialog-actions">
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" disabled={busy || !summary.trim()}>
            {sending ? "Sending…" : "Send request"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function QueueModal({
  item,
  users,
  viewer,
  onClose,
  onSave,
  busy,
  saveError,
}: {
  item?: QueueItem;
  users: { id: string; name: string }[];
  viewer: User;
  onClose: () => void;
  onSave: (
    body: Record<string, unknown>,
    complete?: { disposition: Disposition },
  ) => Promise<boolean>;
  busy: boolean;
  saveError: string;
}) {
  const [title, setTitle] = useState(item?.title ?? "");
  const [contactName, setContactName] = useState(item?.contactName ?? "");
  const [contactUrl, setContactUrl] = useState(item?.contactUrl ?? "");
  const [phone, setPhone] = useState(item?.phone ?? "");
  const [notes, setNotes] = useState(item?.notes ?? "");
  const [dueAt, setDueAt] = useState(
    item?.dueAt
      ? new Date(item.dueAt - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .slice(0, 16)
      : "",
  );
  const [ownerId, setOwnerId] = useState(item?.ownerId ?? viewer.id);
  const [disposition, setDisposition] = useState<Disposition | "">(
    item?.disposition ?? "",
  );
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);
  const close = () => {
    if (!dirty || window.confirm("Discard unsaved task changes?")) onClose();
  };
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!title.trim()) {
      setError("Task title is required.");
      return;
    }
    if (complete && !disposition) {
      setError("Choose a disposition to complete this task.");
      return;
    }
    if (
      complete &&
      disposition === "callback" &&
      (!dueAt || new Date(dueAt).getTime() <= Date.now())
    ) {
      setError("Callback needs a future due time.");
      return;
    }
    const url = contactUrl.trim();
    if (url && !safeUrl(url)) {
      setError("Contact link must be an app.gohighlevel.com HTTPS URL.");
      return;
    }
    setError("");
    void onSave(
      {
        title: title.trim(),
        contactName: contactName.trim(),
        contactUrl: url,
        phone: phone.trim(),
        notes: notes.trim(),
        dueAt: dueAt ? new Date(dueAt).getTime() : null,
        ownerId,
      },
      complete && disposition ? { disposition } : undefined,
    );
  };
  const track =
    <T,>(setter: React.Dispatch<React.SetStateAction<T>>) =>
    (value: T) => {
      setter(value);
      setDirty(true);
    };
  return (
    <ModalShell
      title={item ? "Edit task" : "New queue task"}
      error={saveError}
      description="Task details and dispositions are saved to GHL Connect, not GHL."
      onClose={close}
    >
      <form onSubmit={submit} className="dialog-form">
        <label>
          Task title
          <input
            required
            maxLength={160}
            value={title}
            onChange={(event) => track(setTitle)(event.target.value)}
            autoFocus
          />
        </label>
        <div className="two-fields">
          <label>
            Contact name
            <input
              maxLength={160}
              value={contactName}
              onChange={(event) => track(setContactName)(event.target.value)}
            />
          </label>
          <label>
            Phone or reference
            <input
              maxLength={80}
              value={phone}
              onChange={(event) => track(setPhone)(event.target.value)}
            />
          </label>
        </div>
        <label>
          Contact link
          <input
            type="url"
            placeholder="https://"
            value={contactUrl}
            onChange={(event) => track(setContactUrl)(event.target.value)}
          />
        </label>
        <label>
          Due date and time
          <input
            type="datetime-local"
            value={dueAt}
            onChange={(event) => track(setDueAt)(event.target.value)}
          />
        </label>
        {users.length > 1 && (
          <label>
            Owner
            <select
              value={ownerId}
              onChange={(event) => track(setOwnerId)(event.target.value)}
            >
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          Notes
          <textarea
            rows={5}
            maxLength={4000}
            value={notes}
            onChange={(event) => track(setNotes)(event.target.value)}
          />
        </label>
        {item?.contactUrl && safeUrl(item.contactUrl) && (
          <a
            href={safeUrl(item.contactUrl)!}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open saved contact link
          </a>
        )}
        {(!item || item.status === "open") && (
          <div className="completion-box">
            <label className="check-filter">
              <input
                type="checkbox"
                checked={complete}
                onChange={(event) => track(setComplete)(event.target.checked)}
              />
              Complete with disposition
            </label>
            {complete && (
              <label>
                Disposition
                <select
                  value={disposition}
                  required
                  onChange={(event) =>
                    track(setDisposition)(event.target.value as Disposition)
                  }
                >
                  <option value="">Choose outcome</option>
                  {dispositions.map((value) => (
                    <option key={value} value={value}>
                      {DISPOSITION_LABELS[value]}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}
        {error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}
        <div className="dialog-actions">
          <button type="button" className="secondary" onClick={close}>
            Cancel
          </button>
          <button type="submit" disabled={busy}>
            {busy ? "Saving…" : complete ? "Save and complete" : "Save task"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function ScriptModal({
  item,
  viewer,
  teams,
  onClose,
  onSave,
  busy,
  saveError,
}: {
  item?: DeskScript;
  viewer: User;
  teams: Team[];
  onClose: () => void;
  onSave: (body: {
    title: string;
    content: string;
    teamIds: string[];
  }) => Promise<boolean>;
  busy: boolean;
  saveError: string;
}) {
  const [title, setTitle] = useState(item?.title ?? "");
  const [content, setContent] = useState(item?.content ?? "");
  const [teamIds, setTeamIds] = useState(
    item?.teamIds ?? (viewer.role === "manager" ? viewer.teamIds : []),
  );
  const [dirty, setDirty] = useState(false);
  const close = () => {
    if (!dirty || window.confirm("Discard unsaved script changes?")) onClose();
  };
  return (
    <ModalShell
      title={item ? "Edit script" : "New script"}
      error={saveError}
      description={
        viewer.role === "admin"
          ? "Scripts are plain text and visible to selected teams. No team selection means all authorized users."
          : "Scripts are plain text and visible to selected teams you manage. Select at least one team."
      }
      onClose={close}
    >
      <form
        className="dialog-form"
        onSubmit={(event) => {
          event.preventDefault();
          void onSave({
            title: title.trim(),
            content: content.trim(),
            teamIds,
          });
        }}
      >
        <label>
          Title
          <input
            required
            maxLength={160}
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              setDirty(true);
            }}
            autoFocus
          />
        </label>
        <label>
          Script text
          <textarea
            required
            rows={10}
            maxLength={10000}
            value={content}
            onChange={(event) => {
              setContent(event.target.value);
              setDirty(true);
            }}
          />
        </label>
        <fieldset>
          <legend>Visible to teams</legend>
          <p className="field-hint">
            {viewer.role === "admin"
              ? "Leave all unchecked to share with every authorized user."
              : "Choose one or more of your assigned teams."}
          </p>
          <div className="checkbox-list">
            {teams.map((team) => (
              <label key={team.id}>
                <input
                  type="checkbox"
                  checked={teamIds.includes(team.id)}
                  onChange={(event) => {
                    setTeamIds(
                      event.target.checked
                        ? [...teamIds, team.id]
                        : teamIds.filter((id) => id !== team.id),
                    );
                    setDirty(true);
                  }}
                />
                {team.name}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="dialog-actions">
          <button type="button" className="secondary" onClick={close}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              busy ||
              !title.trim() ||
              !content.trim() ||
              !canEditScript(viewer, { teamIds })
            }
          >
            {busy ? "Saving…" : "Save script"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function UserModal({
  user,
  teams,
  onClose,
  onSave,
  busy,
  saveError,
}: {
  user?: User;
  teams: Team[];
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => Promise<boolean>;
  busy: boolean;
  saveError: string;
}) {
  const [name, setName] = useState(user?.name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>(user?.role ?? "member");
  const [duty, setDuty] = useState<Duty>(user?.duty ?? "opener");
  const [teamIds, setTeamIds] = useState<string[]>(user?.teamIds ?? []);
  const [locationIds, setLocationIds] = useState(
    user?.locationIds.join(", ") ?? "",
  );
  const [active, setActive] = useState(user?.active ?? true);
  const [dirty, setDirty] = useState(false);
  const close = () => {
    if (!dirty || window.confirm("Discard unsaved account changes?")) onClose();
  };
  return (
    <ModalShell
      title={user ? `Edit ${user.name}` : "Add person"}
      error={saveError}
      description="Team, role and approved GHL location access are enforced by GHL Connect."
      onClose={close}
    >
      <form
        className="dialog-form"
        onSubmit={(event) => {
          event.preventDefault();
          const parsedLocations = locationIds
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean);
          void onSave(
            user
              ? {
                  name: name.trim(),
                  role,
                  duty,
                  teamIds,
                  locationIds: parsedLocations,
                  active,
                }
              : {
                  name: name.trim(),
                  email: email.trim(),
                  password,
                  role,
                  duty,
                  teamIds,
                  locationIds: parsedLocations,
                },
          );
        }}
      >
        <label>
          Name
          <input
            required
            maxLength={120}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setDirty(true);
            }}
            autoFocus
          />
        </label>
        {!user && (
          <>
            <label>
              Email
              <input
                type="email"
                required
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setDirty(true);
                }}
              />
            </label>
            <label>
              Initial password
              <input
                type="password"
                required
                minLength={12}
                autoComplete="new-password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setDirty(true);
                }}
              />
              <span className="field-hint">
                At least 12 characters. Share it through your approved channel.
              </span>
            </label>
          </>
        )}
        <div className="two-fields">
          <label>
            Role
            <select
              value={role}
              onChange={(event) => {
                setRole(event.target.value as Role);
                setDirty(true);
              }}
            >
              {roles.map((value) => (
                <option key={value} value={value}>
                  {label(value)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Duty
            <select
              value={duty}
              onChange={(event) => {
                setDuty(event.target.value as Duty);
                setDirty(true);
              }}
            >
              {duties.map((value) => (
                <option key={value} value={value}>
                  {label(value)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <fieldset>
          <legend>Teams</legend>
          <div className="checkbox-list">
            {teams.map((team) => (
              <label key={team.id}>
                <input
                  type="checkbox"
                  checked={teamIds.includes(team.id)}
                  onChange={(event) => {
                    setTeamIds(
                      event.target.checked
                        ? [...teamIds, team.id]
                        : teamIds.filter((id) => id !== team.id),
                    );
                    setDirty(true);
                  }}
                />
                {team.name}
              </label>
            ))}
          </div>
        </fieldset>
        <label>
          Approved GHL location IDs
          <input
            value={locationIds}
            onChange={(event) => {
              setLocationIds(event.target.value);
              setDirty(true);
            }}
            placeholder="Separate IDs with commas"
          />
          <span className="field-hint">
            Only these locations may report observation for this user.
          </span>
        </label>
        {user && (
          <label className="check-filter">
            <input
              type="checkbox"
              checked={active}
              onChange={(event) => {
                setActive(event.target.checked);
                setDirty(true);
              }}
            />
            Account active
          </label>
        )}
        <div className="dialog-actions">
          <button type="button" className="secondary" onClick={close}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              busy ||
              !name.trim() ||
              (!user && (!email.trim() || password.length < 12))
            }
          >
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function TeamModal({
  team,
  onClose,
  onSave,
  busy,
  saveError,
}: {
  team?: Team;
  onClose: () => void;
  onSave: (body: {
    name: string;
    color: string;
    pilotEnabled: boolean;
  }) => Promise<boolean>;
  busy: boolean;
  saveError: string;
}) {
  const [name, setName] = useState(team?.name ?? "");
  const [color, setColor] = useState(team?.color ?? "#245cdb");
  const [pilotEnabled, setPilotEnabled] = useState(team?.pilotEnabled ?? false);
  const [dirty, setDirty] = useState(false);
  const close = () => {
    if (!dirty || window.confirm("Discard unsaved team changes?")) onClose();
  };
  return (
    <ModalShell
      title={team ? `Edit ${team.name}` : "Add team"}
      error={saveError}
      description="Team membership and pilot access are local GHL Connect settings."
      onClose={close}
    >
      <form
        className="dialog-form"
        onSubmit={(event) => {
          event.preventDefault();
          void onSave({ name: name.trim(), color, pilotEnabled });
        }}
      >
        <label>
          Team name
          <input
            required
            maxLength={120}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setDirty(true);
            }}
            autoFocus
          />
        </label>
        <label>
          Team color
          <input
            type="color"
            value={color}
            onChange={(event) => {
              setColor(event.target.value);
              setDirty(true);
            }}
          />
        </label>
        <label className="pilot-control">
          <input
            type="checkbox"
            checked={pilotEnabled}
            onChange={(event) => {
              setPilotEnabled(event.target.checked);
              setDirty(true);
            }}
          />
          <span>
            <strong>Enable pilot for this team</strong>
            <small>
              {pilotEnabled
                ? "Members of this team may access pilot features after saving."
                : "Pilot is off. Members of this team do not get pilot access from this team."}
            </small>
          </span>
        </label>
        {team && pilotEnabled !== team.pilotEnabled && (
          <p className="pilot-impact" role="status">
            {pilotEnabled ? "Enabling" : "Disabling"} pilot changes access for
            members of {team.name}. Members in another pilot-enabled team may
            still have access.
          </p>
        )}
        <div className="dialog-actions">
          <button type="button" className="secondary" onClick={close}>
            Cancel
          </button>
          <button type="submit" disabled={busy || !name.trim()}>
            {busy ? "Saving…" : team ? "Save team" : "Add team"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}
